import { Fragment, ReactElement, useEffect, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import { Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import * as countryFlags from 'country-flag-icons/react/3x2'
import { Input, Modal, Select } from 'components'
import { useBinsService, useWheatCodesService } from 'services'
import { RootState } from 'store'
import { getAttributeWarnings, validateAttributes } from 'utils'
import { TWheatCode, TAttribute } from 'types/models'
import countryList from 'utils/countries'
import { getRangeTooltipMessage } from 'utils/helpers/validationFunctions'
import Dropdown from 'components/ui/Dropdown/Dropdown'
import HelperTextWithTooltip from '../components/WheatCodeList/HelperTextWithTooltip '
import * as sxStyles from './dialogs.style'
import { FormEditableContext } from '../../../../../providers'
import { CrudAction, Entity } from '../../../../../types/permissions'
import { usePermissions } from '../../../../../utils/hooks'
import { useRoles } from '../../../../../utils/hooks/useRoles'
import { useSystemSettings } from '../../../../../utils/hooks/useSystemSettings'

const favouriteCountries = [
  { value: 'il', label: 'Israel', icon: <countryFlags.IL /> },
  { value: 'ua', label: 'Ukraine', icon: <countryFlags.UA /> },
  { value: 'ru', label: 'Russia', icon: <countryFlags.RU /> },
  { value: 'us', label: 'United States', icon: <countryFlags.US /> },
  { value: 'ca', label: 'Canada', icon: <countryFlags.CA /> },
  { value: 'gb', label: 'United Kingdom', icon: <countryFlags.GB /> },
]

interface Props {
  onConfirm(data: any): void
  initialData?: Partial<TWheatCode>
  mode?: 'add' | 'edit'
}

const useEditModal = ({
  onConfirm,
  initialData,
  mode = 'add',
}: Props): [ReactElement, VoidFunction] => {
  const { checkPermission } = usePermissions()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [attributesValidationErrors, setAttributesValidationErrors] = useState<
    Record<string, Record<string, string>>
  >({})

  const validationRules = useSelector(
    (state: RootState) => state.validations.validationRules
  )
  const [attributeWarnings, setAttributeWarnings] = useState<
    Record<
      string,
      {
        range_min?: React.ReactNode
        range_max?: React.ReactNode
        range_avg?: React.ReactNode
      }
    >
  >({})
  const { getWheatCodeStatuses } = useWheatCodesService()
  const { getBins } = useBinsService()

  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xl'))
  const isXSmallScreen = useMediaQuery('(min-width:1100px)')
  const isXXSmallScreen = useMediaQuery('(min-width:750px)')

  const { isSuperAdmin, isSuperUser } = useRoles()
  const { isEnabled: displayTargetBin } = useSystemSettings(
    'SHOW_WHEAT_TARGET_BIN'
  )

  const { data: wheatCodeStatuses } = useQuery({
    queryKey: ['wheat_codes_statuses'],
    queryFn: () => getWheatCodeStatuses(),
  })

  const { data: bins } = useQuery({
    queryKey: ['bins_list'],
    queryFn: () => getBins(),
  })

  const statusesOptions = wheatCodeStatuses?.results?.map((item) => ({
    label: item?.name,
    value: item?.id,
  }))

  const binsOptions = [
    { label: 'No bin selected', value: '' },
    ...(bins?.results?.map((item) => ({
      label: item?.name,
      value: item?.id?.toString(),
    })) || []),
  ]

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TWheatCode>({
    defaultValues: {
      code: initialData?.code || '',
      vessel: initialData?.vessel || '',
      origin: initialData?.origin || '',
      amount: initialData?.amount || undefined,
      attributes: initialData?.attributes || [],
      cost: initialData?.cost || 0,
      status_id: initialData?.status_id || undefined,
      target_bin_id: initialData?.target_bin_id ?? '',
      target_bin_name: initialData?.target_bin_name ?? 'No bin selected',
    },
  })

  const deepEqual = (obj1: any, obj2: any) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2)
  }

  const resetRef = useRef<Partial<TWheatCode>>(initialData)

  useEffect(() => {
    if (initialData && !deepEqual(initialData, resetRef.current)) {
      reset(initialData)
      resetRef.current = initialData
    }
  }, [initialData, reset])

  const toggleModal = () => setIsOpen(!isOpen)

  const onSubmit = async (data: TWheatCode) => {
    // Validate attributes using our validation function
    const validationErrors = await validateAttributes(
      data.attributes,
      validationRules
    )
    const warnings = getAttributeWarnings(data.attributes, validationRules)
    setAttributeWarnings(warnings)

    // Check if there are any validation errors
    if (Object.keys(validationErrors).length > 0) {
      // If there are errors, set them and return early
      setAttributesValidationErrors(validationErrors)
      return
    }

    // If no errors, proceed with submission
    setAttributesValidationErrors({})
    setAttributeWarnings({})
    if (data.target_bin_id === '') data.target_bin_id = null
    if (data.target_bin_id !== initialData?.target_bin_id && mode === 'edit') {
      data.is_recalculate_needed = true
    } else {
      data.is_recalculate_needed = false
    }
    onConfirm(data)
    toggleModal()
    reset()
  }

  const validateInput = async (updatedParams: TAttribute[]) => {
    const validationResults = await validateAttributes(
      updatedParams,
      validationRules
    )
    const warnings = getAttributeWarnings(updatedParams, validationRules)
    setAttributesValidationErrors(validationResults)
    setAttributeWarnings(warnings)
  }

  const isReadOnly =
    mode !== 'add'
      ? !checkPermission(Entity.WHEAT_CODE, CrudAction.UPDATE)
      : !checkPermission(Entity.WHEAT_CODE, CrudAction.CREATE)

  return [
    <FormEditableContext.Provider
      value={{
        isEditable: !isReadOnly,
      }}
    >
      <Modal
        isOpen={isOpen}
        onClose={() => {
          toggleModal()
          reset()
        }}
        title={mode === 'add' ? 'Add Wheat Code' : 'Edit Wheat Code'}
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonDisabled={isReadOnly}
        onConfirmButtonText="Save"
      >
        <Box px="24px" py="20px" sx={sxStyles.mainBox}>
          <Grid container spacing={isXSmallScreen ? 2 : 5}>
            <Grid size={isXSmallScreen ? 4 : 12}>
              <Stack flex={4} sx={sxStyles.stackStyle}>
                <Controller
                  name="code"
                  control={control}
                  rules={{ required: 'Wheat Code is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label="Wheat Code"
                      placeholder="Wheat Code"
                      error={!!errors.code}
                      helperText={errors.code?.message}
                    />
                  )}
                />
                <Controller
                  name="vessel"
                  control={control}
                  rules={{ required: 'Vessel is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label="Vessel"
                      placeholder="Vessel"
                      error={!!errors.vessel}
                      helperText={errors.vessel?.message}
                    />
                  )}
                />
                <Controller
                  name="origin"
                  control={control}
                  rules={{ required: 'Country of origin is required' }}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={countryList}
                      favourites={favouriteCountries}
                      label="Country of Origin"
                      searchable
                      error={!!errors.origin}
                      helperText={errors.origin?.message}
                    />
                  )}
                />
                <Controller
                  name="amount"
                  control={control}
                  rules={{
                    required: 'Amount is required',
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label="Amount"
                      placeholder="Amount"
                      type="number"
                      error={!!errors.amount}
                      helperText={errors.amount?.message}
                    />
                  )}
                />
                <Controller
                  name={`status_id`}
                  control={control}
                  rules={{
                    required: 'Status is required',
                  }}
                  render={({ field }) => (
                    <Select
                      options={statusesOptions}
                      label="Status"
                      placeholder="Status"
                      error={!!errors.status_id}
                      helperText={errors.status_id?.message}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(val) => field.onChange(val ?? '')}
                    />
                  )}
                />
                {(isSuperAdmin || isSuperUser) && displayTargetBin && (
                  <Controller
                    name="target_bin_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={binsOptions}
                        label="Target Bin"
                        placeholder="Target Bin"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(val) => field.onChange(val ?? '')}
                      />
                    )}
                  />
                )}
              </Stack>
            </Grid>
            <Grid size={isXSmallScreen ? 8 : 12}>
              <Box
                flex={6}
                sx={
                  isXSmallScreen
                    ? sxStyles.smallScreenStackBoxWrapper
                    : sxStyles.stackBoxWrapper
                }
              >
                <Stack
                  spacing="3px"
                  sx={
                    isXSmallScreen
                      ? sxStyles.smallScreenStackFont
                      : sxStyles.stackFont
                  }
                >
                  <Stack
                    direction="row"
                    spacing={3}
                    height={48}
                    px="18px"
                    sx={sxStyles.paramsMainBoldStack}
                  >
                    <Box flex={1}>Parameter</Box>
                    <Box flex={3} sx={sxStyles.stackBoxWraper}>
                      <Stack
                        direction="row"
                        spacing={4.5}
                        sx={sxStyles.valuesStack}
                      >
                        <Box
                          width={
                            isSmallScreen ? (isXXSmallScreen ? 80 : 60) : 120
                          }
                        >
                          Min
                        </Box>
                        <Box
                          width={
                            isSmallScreen ? (isXXSmallScreen ? 80 : 60) : 120
                          }
                          sx={sxStyles.maxValuesBox}
                        >
                          Max
                        </Box>
                      </Stack>
                    </Box>
                    <Box
                      width={isSmallScreen ? (isXXSmallScreen ? 80 : 60) : 120}
                    >
                      Average
                    </Box>
                  </Stack>
                  <Controller
                    name="attributes"
                    control={control}
                    rules={{ required: 'Attributes are required' }}
                    render={({ field }) => (
                      <Fragment>
                        {field.value?.map(
                          (param: TAttribute, index: number) => (
                            <Stack
                              key={index}
                              direction="row"
                              spacing={3}
                              height={48}
                              px="18px"
                              sx={sxStyles.paramsMainStack}
                            >
                              <Box flex={1}>{param.name}</Box>
                              <Box flex={3} sx={sxStyles.stackBoxWraper}>
                                <Stack
                                  direction="row"
                                  spacing={3}
                                  sx={sxStyles.valuesStack}
                                >
                                  <Box
                                    width={
                                      isSmallScreen
                                        ? isXXSmallScreen
                                          ? 80
                                          : 60
                                        : 120
                                    }
                                  >
                                    <Tooltip
                                      title={getRangeTooltipMessage(
                                        validationRules,
                                        param.name,
                                        'range_min'
                                      )}
                                      arrow
                                      placement="top"
                                    >
                                      <Input
                                        size="small"
                                        value={param.range_min ?? 0}
                                        onChange={async (e) => {
                                          const updatedParams = [...field.value]
                                          updatedParams[index].range_min =
                                            e.target.value.trim()
                                          field.onChange(updatedParams)
                                          await validateInput(updatedParams)
                                        }}
                                        onBlur={(e) => {
                                          const value = parseFloat(
                                            e.target.value
                                          )
                                          const updatedParams = [...field.value]
                                          updatedParams[index].range_min =
                                            isNaN(value) ? 0 : value
                                          field.onChange(updatedParams)
                                        }}
                                        fullWidth
                                        error={
                                          !!attributesValidationErrors[
                                            param.name
                                          ]?.range_min
                                        }
                                        helperText={
                                          <HelperTextWithTooltip
                                            text={
                                              attributesValidationErrors[
                                                param.name
                                              ]?.range_min ||
                                              attributeWarnings[
                                                param.name
                                              ]?.range_min?.toString() ||
                                              ''
                                            }
                                            isRight={true}
                                            isWarning={
                                              !!attributeWarnings[param.name]
                                                ?.range_min
                                            }
                                          />
                                        }
                                        sx={sxStyles.helperTextStyle}
                                      />
                                    </Tooltip>
                                  </Box>
                                  <Box>-</Box>
                                  <Box
                                    width={
                                      isSmallScreen
                                        ? isXXSmallScreen
                                          ? 80
                                          : 60
                                        : 120
                                    }
                                  >
                                    <Tooltip
                                      title={getRangeTooltipMessage(
                                        validationRules,
                                        param.name,
                                        'range_max'
                                      )}
                                      arrow
                                      placement="top"
                                    >
                                      <Input
                                        size="small"
                                        value={param.range_max ?? 0}
                                        onChange={async (e) => {
                                          const updatedParams = [...field.value]
                                          updatedParams[index].range_max =
                                            e.target.value.trim()
                                          field.onChange(updatedParams)
                                          await validateInput(updatedParams)
                                        }}
                                        onBlur={(e) => {
                                          const value = parseFloat(
                                            e.target.value
                                          )
                                          const updatedParams = [...field.value]
                                          updatedParams[index].range_max =
                                            isNaN(value) ? 0 : value
                                          field.onChange(updatedParams)
                                        }}
                                        fullWidth
                                        error={
                                          !!attributesValidationErrors[
                                            param.name
                                          ]?.range_max
                                        }
                                        helperText={
                                          <HelperTextWithTooltip
                                            text={
                                              attributesValidationErrors[
                                                param.name
                                              ]?.range_max ||
                                              attributeWarnings[
                                                param.name
                                              ]?.range_max?.toString() ||
                                              ''
                                            }
                                            isRight={false}
                                            isWarning={
                                              !!attributeWarnings[param.name]
                                                ?.range_max
                                            }
                                          />
                                        }
                                        sx={sxStyles.helperTextStyle}
                                      />
                                    </Tooltip>
                                  </Box>
                                </Stack>
                              </Box>
                              <Box
                                width={
                                  isSmallScreen
                                    ? isXXSmallScreen
                                      ? 80
                                      : 60
                                    : 120
                                }
                              >
                                <Tooltip
                                  title={getRangeTooltipMessage(
                                    validationRules,
                                    param.name,
                                    'range_avg'
                                  )}
                                  arrow
                                  placement="top"
                                >
                                  <Input
                                    size="small"
                                    value={param.range_avg ?? 0}
                                    onChange={async (e) => {
                                      const updatedParams = [...field.value]
                                      updatedParams[index].range_avg =
                                        e.target.value.trim()
                                      field.onChange(updatedParams)
                                      await validateInput(updatedParams)
                                    }}
                                    onBlur={async (e) => {
                                      const value = parseFloat(e.target.value)
                                      const updatedParams = [...field.value]
                                      updatedParams[index].range_avg = isNaN(
                                        value
                                      )
                                        ? 0
                                        : value
                                      field.onChange(updatedParams)
                                    }}
                                    fullWidth
                                    error={
                                      !!attributesValidationErrors[param.name]
                                        ?.range_avg
                                    }
                                    helperText={
                                      <HelperTextWithTooltip
                                        text={
                                          attributesValidationErrors[param.name]
                                            ?.range_avg ||
                                          attributeWarnings[
                                            param.name
                                          ]?.range_avg?.toString() ||
                                          ''
                                        }
                                        isRight={false}
                                        isWarning={
                                          !!attributeWarnings[param.name]
                                            ?.range_avg
                                        }
                                      />
                                    }
                                    sx={sxStyles.helperTextStyle}
                                  />
                                </Tooltip>
                              </Box>
                            </Stack>
                          )
                        )}
                      </Fragment>
                    )}
                  />
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </FormEditableContext.Provider>,
    toggleModal,
  ]
}

export default useEditModal
